<template>
  <div>
    <div class="printers-table__item" :class="{'active':activeClass, 'expanded':printer.filament_tracking==='t', 'expanded':showTempsBlock, 'closed':!showJobs}" >
      <div class="top-bar">
        <div class="top-bar--left">
          <h4>
            <printer-status v-if="!isIndustrial" :printer="printer" style="cursor: pointer;" @click="openPrinterLog=true"></printer-status>
            <printer-status v-else :printer="printer"></printer-status>
            {{calculatePrinterName(printer)}} <span class="model">{{printer.description}}</span>
            <printer-warning-flags :printer="printer"></printer-warning-flags>
          </h4>
          <printer-inline-note :note="printer.notes" v-if="canPrint(printer.printer_ownership_id)"></printer-inline-note>
        </div>
        <div class="top-bar--mid">
          <p class="printers-table__item__filaments" v-if="printer.filament_tracking==='t'">
            <printer-filament-labels :printer="printer"></printer-filament-labels>
          </p>
          <p class="printers-table__item__temperature" v-if="showTempsBlock">
            <printer-temperatures :printer="printer"></printer-temperatures>
          </p>
        </div>
        <div class="top-bar--right">
          <printer-build-tray-is-clear-button :printer="printer" v-if="showBuildTrayButton"></printer-build-tray-is-clear-button>
          <printer-action-buttons :printer="printer"
                                  :job-sorting="jobSorting"
                                  :jobs-in-queue-count="queueJobsCount"
                                  @menuOpened="activeClass=true"
                                  @menuClosed="activeClass=false"
                                  @openPrinterLog="openPrinterLog=true"
                                  @toggleJobSorting="handleToggleJobSorting">
          </printer-action-buttons>
        </div>
      </div>
    </div>

    <div class="printers-table__item__queue" v-if="jobsInProgress.length > 0" aria-expanded="true">
      <p><span class="text--muted">{{$t('printers.item.printing')}} : </span> <span class="text--600"> {{$t('printers.item.timeLeft')}} </span> <span class="text--muted"> ~ </span> <human-duration :seconds="printingTimeLeft"></human-duration></p>
    </div>
    <b-collapse visible v-if="jobsInProgress.length > 0" class="collapse-jobs--in-progress">
      <job-table :printer="printer" :jobs="jobsInProgress" :sortable="jobSorting"></job-table>
    </b-collapse>

    <div class="printers-table__item__queue" :class="{'collapsed': !showJobs}" v-if="jobsNotInProgress.length > 0"
         :aria-expanded="showJobs ? 'true' : 'false'" :aria-controls="jobsCollapseId" @click="showJobs = !showJobs">
      <p><span class="text--muted">{{$t('printers.item.queue')}} : </span> <span class="text--600"> {{queueJobsCountText}} </span> <span class="text--muted"> ~ </span> <human-duration :seconds="queueJobsTime"></human-duration></p>
    </div>
    <b-collapse :id="jobsCollapseId" v-model="showJobs">
      <job-table v-if="jobsNotInProgress.length > 0" :printer="printer" :jobs="jobsNotInProgress" :sortable="jobSorting"></job-table>
    </b-collapse>

    <b-modal v-model="openPrinterLog" size="xl">
      <printer-log :printer-id="printer.printer_id"></printer-log>
      <template v-slot:modal-footer="{ cancel }">
        <div class="modal__footer--left">
          <b-button variant="-gray" @click="cancel()">
            {{$t('printers.modal.printerLog.close')}}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex';
  import Printer from '@/mixins/printer';
  import DateTime from '@/mixins/dateTime';
  import PrinterStatus from './printer/PrinterStatus';
  import PrinterFilamentLabels from './printer/PrinterFilamentLabels';
  import PrinterTemperatures from './printer/PrinterTemperatures';
  import PrinterWarningFlags from './printer/PrinterWarningFlags';
  import PrinterActionButtons from './printer/PrinterActionButtons';
  import PrinterBuildTrayIsClearButton from './printer/PrinterBuildTrayIsClearButton';
  import HumanDuration from "../HumanDuration";

  import JobTable from "./job/JobTable";
  import PrinterInlineNote from "./printer/PrinterInlineNote";
  import PrinterLog from "./modal/PrinterLog";

  export default {
    name: 'printer-item',
    props: ['printer'],
    mixins: [DateTime, Printer],
    data() {
      return {
        jobSorting: false,
        showJobs: true,
        activeClass: false,
        openPrinterLog: false
      };
    },
    components: {
      PrinterLog,
      PrinterInlineNote,
      PrinterStatus,
      PrinterFilamentLabels,
      PrinterTemperatures,
      PrinterWarningFlags,
      PrinterActionButtons,
      PrinterBuildTrayIsClearButton,
      JobTable,
      HumanDuration
    },
    computed: {
      ...mapGetters(['isPrintingState', 'isUserInOrganisation', 'userData', 'getJobsByPrinter']),

      showTempsBlock() {
        return this.printer.temps && this.printer.state !== 'offline';
      },
      showBuildTrayButton() {
        if (!this.canAccessAsOwner(this.printer.printer_ownership_id) &&
            !(this.isUserInOrganisation && this.userData.advanced && this.userData.advanced_flags.advanced_user_clear_build_tray)
        ) {
          return false;
        }
        if (this.printer.bed_not_clear !== undefined) {
          return true;
        }
        return this.printer.clear_bed_notification === 't';
      },
      jobs() {
        return this.getJobsByPrinter(this.printer.printer_id);
      },
      jobsInProgress() {
        return this.jobs.filter(job => job.job_status_id > 11 && job.job_status_id < 40);
      },
      jobsNotInProgress() {
        return this.jobs.filter(job => job.job_status_id <= 11 || job.job_status_id >= 40);
      },
      queueJobsCount() {
        return this.jobsNotInProgress.length;
      },
      queueJobsCountText() {
        const count = this.queueJobsCount;
        return count + ' ' + this.$tc('printers.item.job', count);
      },
      queueJobsTime() {
        let duration = 0;
        this.jobs.map(job => {
          if (job.duration) {
            if (job.job_status_id == 11) {
              duration += parseInt(job.duration);
            } else if (job.job_status_id == 21) {
              let percent = parseInt(this.printer.percent);
              if (isNaN(percent)) {
                percent = 0;
              }
              duration += parseInt(job.duration) - (parseInt(job.duration) * percent / 100);
            }
          }
        });
        if (!duration) {
          return 0;
        }
        return duration;
      },

      jobsCollapseId() {
        return 'job-collapse' + this.printer.printer_id;
      },

      isIndustrial() {
        return this.printer.state === 'Virtual';
      },

      printingTimeLeft() {
        if (!this.jobsInProgress.length) {
          return 0;
        }
        const timeToPrint = this.jobsInProgress[0].time_to_print;
        return Math.max(
          !this.printer.percent || this.printer.percent == 0 ?
            timeToPrint : Math.round(timeToPrint * (1 - this.printer.percent / 100)),
          0
        );
      }
    },
    methods: {
      handleToggleJobSorting() {
        this.jobSorting = !this.jobSorting;
      }
    }
  }
</script>
<style lang="scss">
  .printers-table__item__queue button {
    font-weight: 100;
    text-transform: lowercase;
  }
  .collapse-jobs--in-progress {
    margin-bottom: 0 !important;
    table {
      border-radius: 0 !important;
    }
  }
</style>
